<template>
  <form @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <FormDynamicFields
      v-model="fields"
      :fields="formFields"
      :loading="loading"
      :validation="validation"
      container-class="col-md-12"
    />
    <div class="form-buttons">
      <Btn variant="primary" type="submit" :disabled="!isSubmittable()" :loading="loading">
        {{ $t("global.submit") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormDynamicFields from "@/components/display/form/FormDynamicFields";
import FormMessage from "@/components/display/form/FormMessage";
import { fromServer } from "@/utils/translations";
import { planCancellation as setupRequiredFields } from "leatherman-js/utils/validations/forms/plan";
import store from "@/store";
import useValidator from "@/utils/useValidator";

/**
 * Form with dynamic fields described in props.formFields
 */
export default {
  name: "CancellationForm",
  components: {
    FormDynamicFields,
    FormMessage,
    Btn,
  },
  props: {
    serviceId: Number,
    serviceType: String,
    formFields: Array,
  },
  setup(props, { emit, root }) {
    const loading = ref(false);
    const errors = ref(null);
    // Build model where each field has an id instead of name
    const fields = reactive(
      props.formFields.reduce((fields, field) => ({ ...fields, [field.id]: null }), {})
    );
    // Dynamically setup validations rules using method instead of predefined object
    const validationRules = setupRequiredFields(
      props.formFields.filter((field) => field.required === "yes")
    );

    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      props.formFields.reduce(
        (fields, field) => ({
          ...fields,
          [field.id]: {
            required: root.$t("validation.required-field"),
            range: root.$t("validation.required-range", { min: field.min, max: field.max }),
            integer: root.$t("validation.required-integer"),
          },
        }),
        {}
      )
    );
    const cancel = () => emit("close");
    const isSubmittable = () => {
      return (
        !loading.value &&
        validation.errors.length === 0 &&
        Object.values(validation.fields).filter((field) => field.dirty).length
      );
    };
    const submit = () => {
      errors.value = null;
      validation
        .validate()
        .then(() => {
          loading.value = true;
          store
            .dispatch(`${props.serviceType}/cancelService`, {
              cancel_field: fields,
              service_id: props.serviceId,
            })
            .then(() => {
              emit("success");
              store.dispatch("toasts/addToast", {
                variant: "success",
                message: root.$t("toasts.request-cancellation-success", {
                  name: props.resourceName,
                }),
              });
            })
            .catch((e) => {
              errors.value = fromServer(e.request.response);
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {});
    };
    return {
      errors,
      fields,
      loading,
      validation,
      submit,
      cancel,
      isSubmittable,
    };
  },
};
</script>

<style lang="scss" scoped></style>
